import React, { useCallback, useRef, useState } from "react";
import {
  resolveLocalizedEntry,
  langAttribute,
} from "../../../utils/questionUtils";
import { strings } from "../../../resources/strings";
import {
  IdentifiableAnswerV3Input,
  LocalizedParticipantQuestion,
  QuestionType,
  WorkflowName,
} from "../../../apis/API";
import { WorkflowItem, WorkflowSpec } from "./types";
import { serializeWorkflowAnswers } from "./workflowAnswers";
import { resolveSeedItems } from "./historyPrefill";
import {
  resolveRepeatableListLabel,
  withRequiredSuffix,
} from "./workflowLabels";
import {
  AnswerIcon,
  CHECK_PATH,
  CLOSE_PATH,
  answerButtonClass,
  saveButtonClass,
} from "./answerButtons";
import FloatingLabelField from "./FloatingLabelField";
import AnswerButtonRow from "./AnswerButtonRow";
import ChangeAnswerButton from "./ChangeAnswerButton";
import type { WorkflowAnswer } from "./WorkflowQuestion";

const wf = strings.reportForm.workflow;

/** One list entry (a single-field value) tracked by a stable client id. */
interface Row {
  id: number;
  value: string;
}

export interface RepeatableListFormProps {
  question: LocalizedParticipantQuestion;
  /** Resolved workflow name (medications | school | … | householdMembers). */
  workflowName: WorkflowName;
  /** Resolved YES_NO_REPEATABLE_LIST or DIRECT_LIST spec (single field). */
  spec: WorkflowSpec;
  /** Prior answer, for back-nav re-display (root selection + entered values). */
  defaultAnswer?: IdentifiableAnswerV3Input;
  /**
   * Raw `questionPrepopulation.values` for this workflow. Read only through the
   * `historyPrefill` helpers, which apply the history-eligibility list.
   */
  prepopulationValues?: string;
  onAnswer: (answer: WorkflowAnswer) => void;
}

/**
 * Repeatable-list workflow UI (Android `RepeatableListForm`). Single text field
 * per entry with "Add another", in two variants:
 *
 * - **YES_NO_REPEATABLE_LIST** (`medications`, `school`, the 5
 *   treatment/program lists): Yes/No root; Yes reveals the list, No records
 *   `answer:false` + `{fieldKey: []}`.
 * - **DIRECT_LIST** (`householdMembers`, `branchOpensOn:"ALWAYS"`): no root; the
 *   list shows immediately with "Save & Continue" and an "I live alone - skip"
 *   control. Emits `answer:null`; zero entries / skip → `{fieldKey: []}`.
 *
 * Entries serialize as columnar single-field arrays (`{fieldKey: [v1, v2, …]}`,
 * empty entries dropped), matching the Android `V3AnswerAssembler` wire contract.
 * The Yes-gated variant shows a "Change my answer" control on the list step back
 * to the Yes/No root (EHA-903); DIRECT_LIST has no root, so it omits it.
 */
const RepeatableListForm: React.FC<RepeatableListFormProps> = ({
  question,
  workflowName,
  spec,
  defaultAnswer,
  prepopulationValues,
  onAnswer,
}) => {
  const questionId = question.id ?? "";
  const wd = question.workflowDefinition;
  const isDirect = spec.template === "DIRECT_LIST";
  const fieldKey = spec.fields[0].fieldKey;
  const headlineEntry = resolveLocalizedEntry(question.localization);
  const headline = headlineEntry?.text ?? "";
  // SC 3.1.2 — the resolver falls back across languages, so text can arrive in
  // a language the document does not declare. undefined when it matches.
  const headlineLang = langAttribute(headlineEntry?.locale);

  // Seed once per mount (the flow remounts the form on every navigation) from
  // the in-session answer, else the participant's last submitted answer.
  // Always ≥1 row.
  const [rows, setRows] = useState<Row[]>(() => {
    const items = resolveSeedItems({
      spec,
      workflowName,
      defaultAnswer,
      prepopulationValues,
    });
    if (items.length === 0) {
      return [{ id: 0, value: "" }];
    }
    return items.map((item, i) => ({ id: i, value: item[fieldKey] }));
  });
  // Next stable row id (never the array index — Remove must not reuse a row's id).
  const nextId = useRef(rows.length);

  // DIRECT_LIST opens straight on the list; YES_NO_REPEATABLE_LIST on the root.
  const [step, setStep] = useState<"root" | "list">(isDirect ? "list" : "root");
  const rootAnswer = defaultAnswer?.answer ?? null;

  const entryTitle =
    resolveRepeatableListLabel(workflowName, wd, "title") ?? headline;
  // Singular per-entry label (e.g. "Household Member") — signals one item per box.
  const fieldLabel =
    resolveRepeatableListLabel(workflowName, wd, "field") ?? fieldKey;
  const addMoreLabel =
    resolveRepeatableListLabel(workflowName, wd, "addMore") ?? wf.addAnother;
  const saveLabel = wf.saveContinue;
  const skipLabel =
    resolveRepeatableListLabel(workflowName, wd, "skip") ?? wf.skip;

  const emit = (answer: boolean | null, items: WorkflowItem[]) =>
    onAnswer({
      questionId,
      questionType: QuestionType.workflow,
      workflowName,
      answer,
      workflowAnswers: serializeWorkflowAnswers(workflowName, spec, items),
    });

  const handleChange = useCallback((rowId: number, value: string) => {
    setRows((prev) => prev.map((r) => (r.id === rowId ? { ...r, value } : r)));
  }, []);

  const handleAddRow = () =>
    setRows((prev) => [...prev, { id: nextId.current++, value: "" }]);

  // Remove renders only at ≥2 rows, so filtering always leaves ≥1 (UI invariant).
  const handleRemoveRow = (rowId: number) =>
    setRows((prev) => prev.filter((r) => r.id !== rowId));

  // Non-empty (trimmed) entries, in order — empties are dropped before emit.
  const filledItems = (): WorkflowItem[] =>
    rows
      .map((r) => r.value.trim())
      .filter((v) => v.length > 0)
      .map((v) => ({ [fieldKey]: v }));

  // Save requires ≥1 entry for both variants; a DIRECT_LIST's zero-member case is
  // handled by its dedicated "I live alone - skip" control, not by Save.
  const canSave = filledItems().length > 0;

  // Can't add another entry until the current (last) one has content — avoids
  // spawning trailing empty rows.
  const canAddRow = rows[rows.length - 1].value.trim().length > 0;

  const handleSave = () => emit(isDirect ? null : true, filledItems());
  const handleYes = () => setStep("list");
  const handleNo = () => emit(false, []);
  const handleSkip = () => emit(null, []);

  const listInputs = (
    <div className="flex flex-col gap-4">
      {rows.map((row, rowIndex) => {
        // Numbered singular label per entry, e.g. "Medication #1" (Callie
        // 2026-07-01) — signals one item per box and disambiguates entries.
        // Only the first entry is required (one is enough after choosing Yes),
        // so the "(Required)" marker rides on entry #1 only (IHA-388).
        const rowLabel = withRequiredSuffix(
          `${fieldLabel} #${rowIndex + 1}`,
          spec.fields[0].required && rowIndex === 0,
        );
        return (
          <div key={row.id} className="flex items-end gap-2">
            <FloatingLabelField
              className="flex-1"
              label={rowLabel}
              required={spec.fields[0].required && rowIndex === 0}
              value={row.value}
              testId={`list-field-${row.id}`}
              autoFocus={rowIndex === 0}
              onChange={(v) => handleChange(row.id, v)}
            />
            {rows.length > 1 && (
              <button
                type="button"
                data-testid={`list-remove-${row.id}`}
                aria-label={`${wf.remove} ${rowIndex + 1}`}
                onClick={() => handleRemoveRow(row.id)}
                className="min-h-[44px] px-3 text-sm text-[#515964] underline"
              >
                {wf.remove}
              </button>
            )}
          </div>
        );
      })}

      <button
        type="button"
        data-testid="list-add"
        onClick={handleAddRow}
        disabled={!canAddRow}
        className="min-h-[44px] self-start px-3 font-semibold text-primary disabled:opacity-30"
      >
        + {addMoreLabel}
      </button>
    </div>
  );

  // --- List step (YES_NO_REPEATABLE_LIST after Yes, or DIRECT_LIST directly) ---
  if (step === "list") {
    return (
      <div
        data-testid="repeatable-list-form"
        className="flex flex-col gap-6 overflow-y-auto"
      >
        <h2 className="text-2xl text-black">{entryTitle}</h2>
        {listInputs}

        <button
          type="button"
          data-testid="list-save"
          onClick={handleSave}
          disabled={!canSave}
          // DIRECT_LIST (householdMembers) presents Save + skip as two co-equal
          // neutral choices per Figma; the Yes-gated lists keep the primary CTA.
          className={`${
            isDirect ? answerButtonClass(false) : saveButtonClass
          } disabled:opacity-30`}
        >
          <AnswerIcon testId="save-continue-icon" path={CHECK_PATH} />
          {saveLabel}
        </button>

        {isDirect && (
          <button
            type="button"
            data-testid="list-skip"
            onClick={handleSkip}
            className={answerButtonClass(false)}
          >
            <AnswerIcon testId="skip-icon" path={CLOSE_PATH} />
            {skipLabel}
          </button>
        )}

        {!isDirect && <ChangeAnswerButton onClick={() => setStep("root")} />}
      </div>
    );
  }

  // --- Root step (YES_NO_REPEATABLE_LIST only) ----------------------------
  return (
    <div data-testid="repeatable-list-form" className="flex flex-col gap-6">
      <h2 lang={headlineLang} className="text-2xl text-black">
        {headline}
      </h2>

      <AnswerButtonRow
        onYes={handleYes}
        onNo={handleNo}
        yesSelected={rootAnswer === true}
        noSelected={rootAnswer === false}
        yesTestId="list-yes"
        noTestId="list-no"
      />
    </div>
  );
};

export default RepeatableListForm;
